import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiMonitor, FiWifi, FiVolume2, FiRotateCcw, FiArrowLeft, FiCheck } = FiIcons;

const TVSetup = () => {
  const [settings, setSettings] = useState({
    resolution: '1920x1080',
    orientation: 'landscape',
    volume: 50,
    autoStart: true,
    syncInterval: 30
  });

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="min-h-screen px-8 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-8"
        >
          <div className="flex items-center space-x-4">
            <Link
              to="/tv"
              className="p-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="text-white" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">Display Settings</h1>
              <p className="text-white/70">Configure your Android TV display</p>
            </div>
          </div>
          <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiSettings} className="text-white text-xl" />
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6"
          >
            {/* Display Settings */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiMonitor} />
                <span>Display Configuration</span>
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Resolution
                  </label>
                  <select
                    value={settings.resolution}
                    onChange={(e) => handleSettingChange('resolution', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <option value="1920x1080">1920x1080 (Full HD)</option>
                    <option value="3840x2160">3840x2160 (4K)</option>
                    <option value="1366x768">1366x768 (HD)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Orientation
                  </label>
                  <select
                    value={settings.orientation}
                    onChange={(e) => handleSettingChange('orientation', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Volume Level: {settings.volume}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={settings.volume}
                    onChange={(e) => handleSettingChange('volume', parseInt(e.target.value))}
                    className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>
              </div>
            </div>

            {/* Network Settings */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiWifi} />
                <span>Network & Sync</span>
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-white/80 text-sm font-medium mb-2">
                    Sync Interval (seconds)
                  </label>
                  <select
                    value={settings.syncInterval}
                    onChange={(e) => handleSettingChange('syncInterval', parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                  >
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                    <option value={300}>5 minutes</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">Auto-start on boot</p>
                    <p className="text-white/60 text-sm">Automatically start player when TV turns on</p>
                  </div>
                  <button
                    onClick={() => handleSettingChange('autoStart', !settings.autoStart)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      settings.autoStart ? 'bg-purple-500' : 'bg-white/20'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      settings.autoStart ? 'translate-x-6' : 'translate-x-0.5'
                    }`}></div>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Preview & Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Display Preview */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Display Preview</h2>
              
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-8 aspect-video flex items-center justify-center">
                <div className="text-center">
                  <SafeIcon icon={FiMonitor} className="text-4xl text-purple-400 mx-auto mb-4" />
                  <p className="text-white/60 mb-2">{settings.resolution}</p>
                  <p className="text-white/60 text-sm">{settings.orientation}</p>
                </div>
              </div>
            </div>

            {/* System Info */}
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">System Information</h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Device Model</span>
                  <span className="text-white">Android TV Box</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Android Version</span>
                  <span className="text-white">11.0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">App Version</span>
                  <span className="text-white">1.0.0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Connection Status</span>
                  <span className="text-green-400 flex items-center space-x-1">
                    <SafeIcon icon={FiWifi} className="text-sm" />
                    <span>Connected</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300">
                <SafeIcon icon={FiCheck} />
                <span>Save Settings</span>
              </button>

              <button className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors">
                <SafeIcon icon={FiRotateCcw} />
                <span>Reset to Defaults</span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TVSetup;